/**
 * Lighting Detection Service
 * Analyzes video frames to determine lighting quality
 *
 * THRESHOLDS (Simplified - no variance-based pass):
 * - <30%  = BLOCKED (uses variance for messaging only)
 *   - Low variance = "Camera blocked"
 *   - High variance = "Too dark"
 * - 30-44% = PASS (acceptable)
 * - 45-54% = PASS (good)
 * - 55%+ = PASS (excellent)
 */
class LightingDetectionService {
  constructor() {
    this.sampleSize = 80;
    this.canvas = null;
    this.ctx = null;
    this.lumaHistory = [];
    this.varianceHistory = [];
    this.historySize = 5;

    // Thresholds - Simplified logic
    this.LUMA_MINIMUM = 25; // Below this = ALWAYS blocked
    this.LUMA_GOOD_THRESHOLD = 45;
    this.LUMA_EXCELLENT_THRESHOLD = 55;
    this.VARIANCE_THRESHOLD = 150; // Used for messaging only, not pass/fail
  }

  initializeCanvas() {
    if (this.canvas) return;
    this.canvas = document.createElement("canvas");
    this.canvas.width = this.sampleSize;
    this.canvas.height = this.sampleSize;
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true });
  }

  calculateRegionStats(videoElement, x, y, width, height) {
    try {
      if (this.canvas.width < width || this.canvas.height < height) {
        this.canvas.width = Math.max(width, this.sampleSize);
        this.canvas.height = Math.max(height, this.sampleSize);
      }

      this.ctx.drawImage(
        videoElement,
        x,
        y,
        width,
        height,
        0,
        0,
        width,
        height,
      );
      const imageData = this.ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      let totalLuma = 0;
      const pixelCount = data.length / 4;
      const lumaValues = [];

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        lumaValues.push(luma);
        totalLuma += luma;
      }

      const meanLuma = totalLuma / pixelCount;
      let sumSquaredDiff = 0;
      for (const luma of lumaValues) {
        sumSquaredDiff += Math.pow(luma - meanLuma, 2);
      }
      const variance = sumSquaredDiff / pixelCount;

      return { luma: meanLuma, variance };
    } catch (error) {
      console.error("Error calculating region stats:", error);
      return { luma: 0, variance: 0 };
    }
  }

  detectLighting(videoElement, faceBoundingBox = null) {
    if (!videoElement || videoElement.readyState < 2) {
      return null;
    }

    this.initializeCanvas();

    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) {
      return null;
    }

    let stats;

    const MIN_FACE_SIZE = 80;
    const useFaceSampling =
      faceBoundingBox &&
      faceBoundingBox.width >= MIN_FACE_SIZE &&
      faceBoundingBox.height >= MIN_FACE_SIZE;

    if (useFaceSampling) {
      stats = this.calculateRegionStats(
        videoElement,
        faceBoundingBox.x,
        faceBoundingBox.y,
        faceBoundingBox.width,
        faceBoundingBox.height,
      );
    } else {
      const regions = [];
      const sampleW = this.sampleSize;
      const sampleH = this.sampleSize;

      regions.push({
        x: (videoWidth - sampleW) / 2,
        y: (videoHeight - sampleH) / 2,
      });
      regions.push({ x: (videoWidth - sampleW) / 2, y: videoHeight * 0.2 });
      regions.push({
        x: videoWidth * 0.25 - sampleW / 2,
        y: (videoHeight - sampleH) / 2,
      });
      regions.push({
        x: videoWidth * 0.75 - sampleW / 2,
        y: (videoHeight - sampleH) / 2,
      });
      regions.push({
        x: videoWidth * 0.25 - sampleW / 2,
        y: videoHeight * 0.25 - sampleH / 2,
      });
      regions.push({
        x: videoWidth * 0.75 - sampleW / 2,
        y: videoHeight * 0.25 - sampleH / 2,
      });

      const allStats = regions.map((region) =>
        this.calculateRegionStats(
          videoElement,
          Math.max(0, region.x),
          Math.max(0, region.y),
          Math.min(sampleW, videoWidth - region.x),
          Math.min(sampleH, videoHeight - region.y),
        ),
      );

      const maxLuma = Math.max(...allStats.map((s) => s.luma));
      const maxVariance = Math.max(...allStats.map((s) => s.variance));
      stats = { luma: maxLuma, variance: maxVariance };
    }

    const normalizedLuma = (stats.luma / 255) * 100;

    this.lumaHistory.push(normalizedLuma);
    this.varianceHistory.push(stats.variance);
    if (this.lumaHistory.length > this.historySize) {
      this.lumaHistory.shift();
      this.varianceHistory.shift();
    }

    let weightedLumaSum = 0;
    let weightedVarianceSum = 0;
    let weightSum = 0;
    for (let i = 0; i < this.lumaHistory.length; i++) {
      const weight = i + 1;
      weightedLumaSum += this.lumaHistory[i] * weight;
      weightedVarianceSum += this.varianceHistory[i] * weight;
      weightSum += weight;
    }
    const smoothedLuma = weightedLumaSum / weightSum;
    const smoothedVariance = weightedVarianceSum / weightSum;

    let quality;
    let isBlocked = false;
    let statusMessage;

    if (smoothedLuma < this.LUMA_MINIMUM) {
      isBlocked = true;
      if (smoothedVariance < this.VARIANCE_THRESHOLD) {
        quality = "blocked";
        statusMessage = "Camera blocked";
      } else {
        quality = "poor";
        statusMessage = "Too dark";
      }
    } else if (smoothedLuma >= this.LUMA_EXCELLENT_THRESHOLD) {
      quality = "excellent";
      statusMessage = "Excellent";
    } else if (smoothedLuma >= this.LUMA_GOOD_THRESHOLD) {
      quality = "good";
      statusMessage = "Good";
    } else {
      quality = "acceptable";
      statusMessage = "Acceptable";
    }

    return {
      luma: Math.round(smoothedLuma),
      variance: Math.round(smoothedVariance),
      quality,
      isBlocked,
      statusMessage,
      rawLuma: Math.round(normalizedLuma),
      rawVariance: Math.round(stats.variance),
    };
  }

  resetHistory() {
    this.lumaHistory = [];
    this.varianceHistory = [];
  }

  dispose() {
    this.canvas = null;
    this.ctx = null;
    this.lumaHistory = [];
    this.varianceHistory = [];
  }
}

export const lightingDetectionService = new LightingDetectionService();
